import { Availability, AvailabilityDaySourceAttribution, ICalFeedSourceRef } from '../types/availability';

export interface AvailabilitySourceBadgeLabels {
  readonly sourceAirbnb: string;
  readonly sourceBooking: string;
  readonly sourceExpedia: string;
  readonly sourceFewoDirekt: string;
  readonly sourceVrbo: string;
  readonly sourceTripadvisor: string;
  readonly sourceOther: string;
  readonly sourceBadge: string;
  readonly formatMultipleSources: (source: string, count: number) => string;
}

type SourceLabelKey =
  | 'sourceAirbnb'
  | 'sourceBooking'
  | 'sourceExpedia'
  | 'sourceFewoDirekt'
  | 'sourceVrbo'
  | 'sourceTripadvisor';

const SOURCE_KEY_TO_LABEL_KEY: Record<string, SourceLabelKey> = {
  AIRBNB: 'sourceAirbnb',
  BOOKING: 'sourceBooking',
  EXPEDIA: 'sourceExpedia',
  FEWO_DIREKT: 'sourceFewoDirekt',
  VRBO: 'sourceVrbo',
  TRIPADVISOR: 'sourceTripadvisor',
};

const resolveSourceLabel = (source: ICalFeedSourceRef, labels: AvailabilitySourceBadgeLabels): string => {
  const labelKey = SOURCE_KEY_TO_LABEL_KEY[source.sourceKey];
  if (labelKey) {
    return labels[labelKey];
  }

  if (source.sourceLabel.trim().length > 0) {
    return source.sourceLabel;
  }

  return labels.sourceOther;
};

export interface AvailabilitySourceBadgeViewModel {
  readonly label: string;
  readonly ariaLabel: string;
  readonly additionalSources: number;
}

export const buildAvailabilitySourceBadge = (
  attribution: AvailabilityDaySourceAttribution,
  labels: AvailabilitySourceBadgeLabels
): AvailabilitySourceBadgeViewModel | null => {
  if (attribution.isDirectAvailability || attribution.icalFeedSources.length === 0) {
    return null;
  }

  const [primarySource] = attribution.icalFeedSources;
  const primaryLabel = resolveSourceLabel(primarySource, labels);
  const additionalSources = attribution.icalFeedSources.length - 1;

  const label =
    additionalSources > 0
      ? labels.formatMultipleSources(primaryLabel, additionalSources)
      : primaryLabel;

  return {
    label,
    ariaLabel: `${labels.sourceBadge}: ${label}`,
    additionalSources,
  };
};

/** Owner-facing categorization of why a slice has its current status, per the 036 mapping rules. */
export type AvailabilityReasonCategory =
  | { readonly kind: 'externallyBooked'; readonly sourceLabel: string }
  | { readonly kind: 'externallyBlocked'; readonly sourceLabel: string }
  | { readonly kind: 'manualBlocked' }
  | { readonly kind: 'platformBooked' }
  | null;

/**
 * Mapping rules (data-model.md):
 * - BOOKED + booking source external => externally booked.
 * - BLOCKED + external iCal reason => externally blocked.
 * - BLOCKED + manual reason => manual blocked.
 * - BOOKED with no external source => platform booked.
 */
export const categorizeAvailabilityReason = (
  availability: Pick<Availability, 'status' | 'blockReason' | 'sourceLabel'>
): AvailabilityReasonCategory => {
  if (availability.status === 'BOOKED') {
    return availability.sourceLabel
      ? { kind: 'externallyBooked', sourceLabel: availability.sourceLabel }
      : { kind: 'platformBooked' };
  }

  if (availability.status === 'BLOCKED') {
    return availability.sourceLabel
      ? { kind: 'externallyBlocked', sourceLabel: availability.sourceLabel }
      : { kind: 'manualBlocked' };
  }

  return null;
};
